import React from "react";
import { useQuery, ApolloError } from "@apollo/client";
import { CardContent, CardHeader, Grid, Card as MuiCard, Typography } from "@material-ui/core";
import { RouteComponentProps } from "@reach/router";
import moment from "moment";
import {
  UserAccountStatistics as UserAccountStatisticsType,
  ApplicationStatistics as ApplicationStatisticsType,
  GetCompletedApplications,
  PropertyCount,
  PMUserCount,
  AdminUserCount,
} from "types/gql-types";
import { Column } from "components/common/html";
import { LoadingSpinner } from "components/graphql-views";
import ApplicationComparisonChart from "./chart";
import { APPLICATION_STATS, GET_COMPLETED_APPLICATIONS, ADMIN_USER_COUNT, PM_USER_COUNT, PROPERTY_COUNT, USER_ACCOUNT_STATS } from "./queries";
import { getCurrentMonth, getCurrentMonthData, getCurrentYear, getLastMonth, getLastMonthData, getLastYear, getLastYearCounts, getThisYearCounts } from "./date-utilities";

interface CountsProps {
  loading: boolean;
  error?: ApolloError;
  data?: {
    total: number;
    createdThisWeek: number;
    createdToday: number;
  }
}

const CountsInfoBox = ({ loading, error, data }: CountsProps) => {
  if (loading) return <LoadingSpinner />;
  if (error || typeof data === "undefined") {
    return <p>Error retrieving user data</p>;
  }

  const { total, createdToday, createdThisWeek } = data;

  return (
    <>
      <Typography variant="body1"><b>Total Applications:</b> { total.toLocaleString() }</Typography>
      <Typography variant="body1"><b>Created Today:</b> { createdToday.toLocaleString() }</Typography>
      <Typography variant="body1"><b>Created this Week:</b> { createdThisWeek.toLocaleString() }</Typography>
    </>
  );
};

const Row = ({ children } : { children?: React.ReactNode }) => (
  <Grid item container spacing={2} xs={12}>
    { children }
  </Grid>
);

const Card = ({ children, header } : { header: string; children?: React.ReactNode }) => (
  <Column width={6}>
    <MuiCard>
      <CardHeader title={header} />
      <CardContent>
        { children }
      </CardContent>
    </MuiCard>
  </Column>
);

const ActivePropertiesCount: React.FC = () => {
  const { data, loading, error } = useQuery<PropertyCount>(PROPERTY_COUNT);
  if (loading) return <LoadingSpinner />;
  if (error || !data) return <>Failed to load properties</>;
  return <Typography variant="body1"><b>Active Properties:</b> {data.propertyCount} </Typography>;
};

const UsersCount: React.FC<{label: string, count: number, loading: boolean}> =
({ label, count, loading }) => {
  if (loading) return <LoadingSpinner />;
  return <Typography variant="body1"><b>{label}:</b> {count.toLocaleString()}</Typography>;
};

const CompletedApplications: React.FC<{ data?: GetCompletedApplications, loading: boolean }> =
({ data, loading }) => {
  if (loading) return <LoadingSpinner />;
  if (!data) return <>Failed to load data</>;

  const currentMonthCount = getCurrentMonthData(data);
  const lastMonthCount = getLastMonthData(data);
  const lastYearCount = getLastYearCounts(data);
  const currentYearCount = getThisYearCounts(data);

  return <>
    <Typography variant="body1"><b>{getLastYear()}:</b> {lastYearCount.toLocaleString()} ({Math.round(lastYearCount / 12).toLocaleString()} / month)</Typography>
    <Typography variant="body1"><b>{getCurrentYear()}:</b> {currentYearCount.toLocaleString()} ({Math.round(currentYearCount / (moment().month() + 1)).toLocaleString()} / month)</Typography>
    <Typography variant="body1"><b>{getCurrentMonth()}:</b> {currentMonthCount.toLocaleString()}</Typography>
    <Typography variant="body1"><b>{getLastMonth()}:</b> {lastMonthCount.toLocaleString()}</Typography>
  </>;
};

const UserDataCounts: React.FC = () => {
  const userQuery = useQuery<UserAccountStatisticsType>(USER_ACCOUNT_STATS);
  // Fetch Admin and PM user counts
  const adminUsersQuery = useQuery<AdminUserCount>(ADMIN_USER_COUNT);
  const pmUsersQuery = useQuery<PMUserCount>(PM_USER_COUNT);
  const adminCount = adminUsersQuery.data?.adminUserCount ?? 0;
  const pmCount = pmUsersQuery.data?.pmUserCount ?? 0;
  const applicantsCount = userQuery.data?.userAccountStatistics.total ?? 0;
  const adjustedTotal = applicantsCount - (adminCount ?? 0) - (pmCount ?? 0);

  return (<>
    <UsersCount label="Applicants" count={adjustedTotal} loading={userQuery.loading || adminUsersQuery.loading || pmUsersQuery.loading} />
    <UsersCount label="Supervisors/PM's" count={pmCount} loading={pmUsersQuery.loading} />
    <UsersCount label="HC Admins" count={adminCount} loading={adminUsersQuery.loading} />
  </>);
};

const DashboardAdmin: React.FC<RouteComponentProps> = () => {
  const [threeFullYearsAgo] =
    new Date(new Date().getFullYear() - 3, 0, 1).toISOString().split("T");

  const applicationQuery = useQuery<ApplicationStatisticsType>(APPLICATION_STATS);
  const { data, loading, error } =
    useQuery<GetCompletedApplications>(
      GET_COMPLETED_APPLICATIONS,
      { variables: { since: threeFullYearsAgo } },
    );

  return (
    <Row>
      <Card header="User Accounts">
        <UserDataCounts />
      </Card>

      <Card header="Completed Applications">
        <CountsInfoBox
          loading={applicationQuery.loading}
          error={applicationQuery.error}
          data={applicationQuery.data?.applicationStatistics}
        />
      </Card>

      <Card header="Historical Completed Applications">
        <CompletedApplications data={data} loading={loading} />
      </Card>

      <Card header="Properties">
        <ActivePropertiesCount />
      </Card>
      <Card header="Completed Applications Chart">
        { loading && <LoadingSpinner /> }
        { error && <p>Error retrieving data</p>}
        { data && data.completedApplicationsByMonth && (
          <ApplicationComparisonChart data={data.completedApplicationsByMonth} />) }
      </Card>
    </Row>
  );
};

export default DashboardAdmin;
